// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Err from "../bindings/Err.js";
import * as Gun from "../bindings/Gun.js";
import * as Types from "../Types.js";
import * as Preact from "../bindings/Preact.js";
import * as Browser from "../bindings/Browser.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";

let gunInstance = {
  contents: undefined
};

let defaultPeers = [
  "https://relay.peer.ooo/gun",
  "https://gun-manhattan.herokuapp.com/gun"
];

function initGun(peers) {
  let gun = gunInstance.contents;
  if (gun !== undefined) {
    return Primitive_option.valFromOption(gun);
  }
  if (!Gun.isGunAvailable()) {
    Err.$$throw("Gun is not loaded. Include gun.js before using this module.");
  }
  let p = Stdlib_Option.getOr(peers, defaultPeers);
  let gun$1 = window.Gun(p);
  gunInstance.contents = Primitive_option.some(gun$1);
  return gun$1;
}

function getGun() {
  let gun = gunInstance.contents;
  if (gun !== undefined) {
    return Primitive_option.valFromOption(gun);
  } else {
    return Err.$$throw("Gun not initialized. Call initGun() first.");
  }
}

function resetGun() {
  gunInstance.contents = undefined;
}

let generateId = Browser.Crypto.generateUUID;

function sanitizeName(name) {
  return name.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll("\"", "&quot;").replaceAll("'", "&#039;");
}

async function createStore(name, options) {
  let trimmedName = name.trim();
  if (trimmedName.length < 1 || trimmedName.length > 50) {
    Err.$$throw("Store name must be 1-50 characters");
  }
  let sanitizedName = sanitizeName(trimmedName);
  let gun = getGun();
  let storeId = generateId();
  let adminId = generateId();
  let timestamp = Date.now();
  let expiryHours = Stdlib_Option.getOr(Stdlib_Option.flatMap(options, o => o.expiryHours), 24);
  let expiryAfterServed = Stdlib_Option.getOr(Stdlib_Option.flatMap(options, o => o.expiryAfterServed), 10);
  let store_settings = {
    expiryHours: expiryHours,
    expiryAfterServed: expiryAfterServed
  };
  let store = {
    id: storeId,
    name: sanitizedName,
    createdAt: timestamp,
    adminId: adminId,
    settings: store_settings,
    currentNumber: 0,
    nowServing: 0,
    servedToday: 0,
    servedLastHour: 0,
    version: "1.0"
  };
  return await new Promise((resolve, reject) => {
    let ref = gun.get("stores").get(storeId);
    ref.put(store, ack => {
      let err = ack.err;
      if (err == null) {
        return resolve({
          storeId: storeId,
          adminId: adminId,
          store: store
        });
      } else {
        return reject(Err.make(err));
      }
    });
  });
}

async function getStore(storeId, timeoutOpt) {
  let timeout = timeoutOpt !== undefined ? timeoutOpt : 5000;
  let gun = getGun();
  return await new Promise((resolve, _reject) => {
    let resolved = {
      contents: false
    };
    let timeoutId = {
      contents: undefined
    };
    let ref_ = gun.get("stores").get(storeId);
    let handleData = (data, _key) => {
      if (data == null) {
        return;
      }
      if (resolved.contents || data.id === "") {
        return;
      }
      resolved.contents = true;
      let id = timeoutId.contents;
      if (id !== undefined) {
        clearTimeout(Primitive_option.valFromOption(id));
      }
      ref_.off();
      resolve(data);
    };
    ref_.on(handleData);
    ref_.once(handleData);
    timeoutId.contents = Primitive_option.some(setTimeout(() => {
      if (!resolved.contents) {
        resolved.contents = true;
        ref_.off();
        return resolve(undefined);
      }
    }, timeout));
  });
}

async function getStoreByAdminId(adminId) {
  let gun = getGun();
  return await new Promise((resolve, _reject) => {
    let found = {
      contents: false
    };
    let ref_ = gun.get("stores");
    ref_.map().once((store, _key) => {
      if (!(store == null) && !found.contents && store.adminId === adminId) {
        found.contents = true;
        return resolve(store);
      }
    });
    setTimeout(() => {
      if (!found.contents) {
        return resolve(undefined);
      }
    }, 2000);
  });
}

async function updateStore(storeId, updates) {
  let gun = getGun();
  await new Promise((resolve, reject) => {
    let ref_ = gun.get("stores").get(storeId);
    ref_.put(updates, ack => {
      let err = ack.err;
      if (err == null) {
        return resolve();
      } else {
        return reject(Err.make(err));
      }
    });
  });
  let store = await getStore(storeId, undefined);
  if (store !== undefined) {
    return store;
  } else {
    return Err.$$throw("Failed to get updated store");
  }
}

function subscribeToStore(storeId, callback) {
  let gun = getGun();
  let ref_ = gun.get("stores").get(storeId);
  ref_.on((data, _key) => {
    if (!(data == null) && data.id !== "") {
      return callback(data);
    }
  });
  return () => {
    ref_.off();
  };
}

async function createTicket(storeId, deviceId) {
  let gun = getGun();
  let storeOpt = await getStore(storeId, undefined);
  let store = storeOpt !== undefined ? storeOpt : Err.$$throw("Store not found");
  let nextNumber = store.currentNumber + 1 | 0;
  let timestamp = Date.now();
  let ticket_servedAt = null;
  let ticket_servedBy = null;
  let ticket = {
    number: nextNumber,
    deviceId: deviceId,
    issuedAt: timestamp,
    status: "waiting",
    servedAt: ticket_servedAt,
    servedBy: ticket_servedBy
  };
  return await new Promise((resolve, reject) => {
    let storeRef = gun.get("stores").get(storeId);
    storeRef.put({
      currentNumber: nextNumber
    }, ack => {
      let err = ack.err;
      if (!(err == null)) {
        return reject(Err.make(err));
      }
      let ticketRef = gun.get("stores").get(storeId).get("tickets").get(nextNumber.toString());
      ticketRef.put(ticket, ack2 => {
        let err = ack2.err;
        if (err == null) {
          return resolve(ticket);
        } else {
          return reject(Err.make(err));
        }
      });
    });
  });
}

async function getTicket(storeId, ticketNumber) {
  let gun = getGun();
  return await new Promise((resolve, _reject) => {
    let ref_ = gun.get("stores").get(storeId).get("tickets").get(ticketNumber.toString());
    ref_.once((data, _key) => {
      if (!(data == null) && data.number > 0) {
        return resolve(data);
      } else {
        return resolve(undefined);
      }
    });
  });
}

async function getTicketByDeviceId(storeId, deviceId) {
  let gun = getGun();
  return await new Promise((resolve, _reject) => {
    let found = {
      contents: false
    };
    let ref_ = gun.get("stores").get(storeId).get("tickets");
    ref_.map().once((ticket, _key) => {
      if (!(ticket == null) && !found.contents && ticket.deviceId === deviceId && ticket.status === "waiting") {
        found.contents = true;
        return resolve(ticket);
      }
    });
    setTimeout(() => {
      if (!found.contents) {
        return resolve(undefined);
      }
    }, 2000);
  });
}

async function getTickets(storeId, status) {
  let gun = getGun();
  let tickets = {
    contents: []
  };
  await new Promise((resolve, _reject) => {
    let ref_ = gun.get("stores").get(storeId).get("tickets");
    ref_.map().once((ticket, _key) => {
      if (ticket == null) {
        return;
      }
      if (ticket.number <= 0) {
        return;
      }
      let shouldInclude = status !== undefined ? ticket.status === status : true;
      if (shouldInclude) {
        tickets.contents = tickets.contents.concat([ticket]);
        return;
      }
    });
    setTimeout(() => resolve(), 500);
  });
  return tickets.contents.toSorted((a, b) => a.number - b.number | 0);
}

async function updateTicket(storeId, ticketNumber, updates) {
  let gun = getGun();
  await new Promise((resolve, reject) => {
    let ref_ = gun.get("stores").get(storeId).get("tickets").get(ticketNumber.toString());
    ref_.put(updates, ack => {
      let err = ack.err;
      if (err == null) {
        return resolve();
      } else {
        return reject(Err.make(err));
      }
    });
  });
  let ticketOpt = await getTicket(storeId, ticketNumber);
  if (ticketOpt !== undefined) {
    return ticketOpt;
  } else {
    return Err.$$throw("Failed to get updated ticket");
  }
}

async function markTicketServed(storeId, ticketNumber, servedByOpt) {
  let servedBy = servedByOpt !== undefined ? servedByOpt : "Self";
  let timestamp = Date.now();
  let servedByStr = Types.servedByToString(servedBy);
  let ticket = await updateTicket(storeId, ticketNumber, {
    status: "served",
    servedAt: timestamp,
    servedBy: servedByStr
  });
  let storeOpt = await getStore(storeId, undefined);
  if (storeOpt !== undefined) {
    let newNowServing = Math.max(storeOpt.nowServing, ticketNumber);
    await updateStore(storeId, {
      nowServing: newNowServing,
      servedToday: storeOpt.servedToday + 1 | 0,
      servedLastHour: storeOpt.servedLastHour + 1 | 0
    });
  }
  return ticket;
}

function subscribeToTickets(storeId, callback) {
  let gun = getGun();
  let ref_ = gun.get("stores").get(storeId).get("tickets");
  ref_.map().on((ticket, _key) => {
    if (!(ticket == null) && ticket.number > 0) {
      return callback(ticket);
    }
  });
  return () => {
    ref_.map().off();
  };
}

function expose() {
  Preact.setWindowProperty("GunHelpers", {
    initGun: initGun,
    getGun: getGun,
    resetGun: resetGun,
    generateId: generateId,
    createStore: createStore,
    getStore: getStore,
    getStoreByAdminId: getStoreByAdminId,
    updateStore: updateStore,
    subscribeToStore: subscribeToStore,
    createTicket: createTicket,
    getTicket: getTicket,
    getTicketByDeviceId: getTicketByDeviceId,
    getTickets: getTickets,
    updateTicket: updateTicket,
    markTicketServed: markTicketServed,
    subscribeToTickets: subscribeToTickets
  });
}

export {
  gunInstance,
  defaultPeers,
  initGun,
  getGun,
  resetGun,
  generateId,
  sanitizeName,
  createStore,
  getStore,
  getStoreByAdminId,
  updateStore,
  subscribeToStore,
  createTicket,
  getTicket,
  getTicketByDeviceId,
  getTickets,
  updateTicket,
  markTicketServed,
  subscribeToTickets,
  expose,
}
/* No side effect */
